import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FileText, 
  ClipboardList, 
  Users, 
  PenTool, 
  Eye,
  Car,
  Search,
  Image,
  Wrench,
  DollarSign,
  TrendingDown,
  AlertCircle,
  BarChart3,
  FileCheck,
  Calculator,
  FolderOpen,
  Clock,
  Mail,
  Menu,
  X,
  ChevronLeft
} from 'lucide-react';

const AkteSidebar = ({ fallId, activeSection, onSectionChange, aktenzeichen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'akte', icon: FileText, label: 'Auftrag', color: 'text-gray-600', number: 1 },
    { id: 'beteiligte', icon: Users, label: 'Versicherung', color: 'text-gray-600', number: 2 },
    { id: 'abtretung', icon: PenTool, label: 'Abtretung', color: 'text-gray-600', number: 3 },
    { id: 'begutachtung', icon: Search, label: 'Begutachtung', color: 'text-gray-600', number: 4 },
    { id: 'lichtbilder', icon: Image, label: 'Lichtbilder', color: 'text-gray-600', number: 5 },
    { id: 'kalkulation', icon: Calculator, label: 'Kalkulation', color: 'text-gray-600', number: 6 },
    { id: 'rechnung', icon: Calculator, label: 'Rechnung', color: 'text-gray-600', number: 7 },
    { id: 'dokumente', icon: FolderOpen, label: 'Dokumente', color: 'text-gray-600', number: 8 },
    { id: 'leer', icon: FileText, label: 'Gutachten', color: 'text-gray-600', number: 9 },
    { id: 'email', icon: Mail, label: 'Email', color: 'text-gray-600', number: 10 }
  ];

  const handleSectionClick = (sectionId) => {
    if (onSectionChange) {
      onSectionChange(sectionId);
    }
    setIsMobileMenuOpen(false);
  };

  const currentSection = sections.find(s => s.id === activeSection);

  return (
    <>
      {/* Mobile Header - nur auf kleinen Bildschirmen sichtbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        
        <div className="flex-1 text-center">
          <span className="font-semibold text-gray-900 text-sm">
            {currentSection?.label || 'Neue Akte'}
          </span>
          {aktenzeichen && (
            <span className="text-xs text-gray-500 block">{aktenzeichen}</span>
          )}
        </div>
        
        <button
          onClick={() => navigate('/gutachten')}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: immer sichtbar, Mobile: Slide-in Menü */}
      <div className={`
        fixed lg:sticky top-0 left-0 z-50 lg:z-auto
        h-screen lg:h-fit
        w-64 bg-white border-r border-gray-200 
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto rounded-r-lg lg:rounded-lg shadow-lg lg:shadow-none
      `}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => navigate('/gutachten')}
            className="hidden lg:flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
          >
            <span>←</span>
            <span>Zurück</span>
          </button>
          
          {/* Mobile: Schließen Button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Schließen</span>
          </button>
          
          {/* Aktenzeichen */}
          {aktenzeichen && (
            <h2 className="text-lg lg:text-xl font-bold text-gray-900 mt-3">{aktenzeichen}</h2>
          )}
          
          <p className="text-xs text-gray-500 mt-1 hidden lg:block">Erfassen Sie die Grunddaten für eine neue Akte</p>
        </div>

        {/* Navigation Items */}
        <nav className="p-2 pb-4 space-y-1.5">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            
            return (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 lg:py-2 rounded-lg transition-all text-left ${
                  isActive
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5 lg:w-4 lg:h-4" />
                <span className="text-sm font-medium">{section.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Spacer für Mobile Header */}
      <div className="lg:hidden h-14" />
    </>
  );
};

export default AkteSidebar;
